/**
 * Sidebar.js
 * Navigation sidebar with team switcher for Data-Next
 */

import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTeam } from "../context/TeamContext";
import {
  FaChartLine,
  FaFolder,
  FaRobot,
  FaHistory,
  FaCog,
  FaChevronDown,
  FaPlus,
} from "react-icons/fa";

export default function Sidebar() {
  const { teams, currentTeam, selectTeam, permissions, createTeam } = useTeam();
  const location = useLocation();
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamDescription, setNewTeamDescription] = useState("");
  const [creating, setCreating] = useState(false);

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) {
      alert("Please enter a team name");
      return;
    }

    try {
      setCreating(true);
      await createTeam(newTeamName, newTeamDescription);
      setShowCreateModal(false);
      setShowTeamDropdown(false);
      setNewTeamName("");
      setNewTeamDescription("");
    } catch (error) {
      console.error("Error creating team:", error);
      alert("Failed to create team: " + error.message);
    } finally {
      setCreating(false);
    }
  };

  const isActive = (path) => location.pathname.includes(path);

  const navItems = [
    {
      path: "/data-next/dashboard",
      label: "Dashboard",
      icon: <FaChartLine />,
      show: true,
    },
    {
      path: "/data-next/context-library",
      label: "Context Library",
      icon: <FaFolder />,
      show: true,
    },
    {
      path: "/data-next/agents",
      label: "Agents",
      icon: <FaRobot />,
      show: permissions.canWrite,
    },
    {
      path: "/data-next/history",
      label: "History",
      icon: <FaHistory />,
      show: true,
    },
    {
      path: "/data-next/team-settings",
      label: "Team Settings",
      icon: <FaCog />,
      show: permissions.canManage,
    },
  ];

  return (
    <div className="data-next-sidebar">
      {/* Header */}
      <div className="sidebar-header">
        <div className="app-title">
          <h2>Data-Next</h2>
          <p className="app-subtitle">AI Diagram Generator</p>
        </div>
      </div>

      {/* Team Selector */}
      {currentTeam && (
        <div className="team-selector-container">
          <button
            className="team-selector-button"
            onClick={() => setShowTeamDropdown(!showTeamDropdown)}
          >
            <div className="team-info">
              <span className="team-name">{currentTeam.teamName}</span>
              <span className="team-role">{permissions.role}</span>
            </div>
            <FaChevronDown
              className={`dropdown-icon ${showTeamDropdown ? "open" : ""}`}
            />
          </button>

          {showTeamDropdown && (
            <div className="team-dropdown">
              <div className="dropdown-header">Switch Team</div>
              {teams.map((team) => (
                <button
                  key={team.id}
                  className={`team-option ${
                    team.id === currentTeam.id ? "active" : ""
                  }`}
                  onClick={() => {
                    selectTeam(team.id);
                    setShowTeamDropdown(false);
                  }}
                >
                  <div className="team-option-info">
                    <span className="team-option-name">{team.teamName}</span>
                    <span className="team-option-role">
                      {team.permissions.isOwner
                        ? "Owner"
                        : team.permissions.isCreator
                        ? "Creator"
                        : "Reader"}
                    </span>
                  </div>
                  {team.id === currentTeam.id && (
                    <span className="check-icon">✓</span>
                  )}
                </button>
              ))}
              <div className="dropdown-divider"></div>
              <button
                className="create-team-button"
                onClick={() => {
                  setShowCreateModal(true);
                  setShowTeamDropdown(false);
                }}
              >
                <FaPlus /> Create New Team
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create Team Modal */}
      {showCreateModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowCreateModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Team</h3>
            <div className="form-group">
              <label>Team Name</label>
              <input
                type="text"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                placeholder="Enter team name"
                className="input"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Description (optional)</label>
              <textarea
                value={newTeamDescription}
                onChange={(e) => setNewTeamDescription(e.target.value)}
                placeholder="What is this team for?"
                className="textarea"
                rows={3}
              />
            </div>
            <div className="modal-actions">
              <button
                onClick={handleCreateTeam}
                disabled={creating || !newTeamName.trim()}
                className="btn btn-primary"
              >
                {creating ? "Creating..." : "Create Team"}
              </button>
              <button
                onClick={() => setShowCreateModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Links */}
      <nav className="sidebar-nav">
        {navItems
          .filter((item) => item.show)
          .map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${isActive(item.path) ? "active" : ""}`}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </Link>
          ))}
      </nav>

      {/* Footer Info */}
      <div className="sidebar-footer">
        <div className="footer-info">
          <p className="version">Version 1.0.0</p>
          <p className="copyright">© 2025 Data-Next</p>
        </div>
      </div>
    </div>
  );
}
